/**
 * The agent's Failover Model Config picker (task 1301), shared by the agent
 * form and the inline agent config editor so both behave the same.
 *
 * The service runs the failover only when the primary's attempts are exhausted
 * by transport errors or invalid replies (task 1300). The picker:
 *
 *   - offers the same options as the primary picker (lifecycle and account
 *     policy marks included), with "No failover" as the empty value;
 *   - lists the primary config disabled, since the service refuses the same
 *     config for both;
 *   - says so when the failover is on the same provider as the primary, the
 *     one case where a provider outage takes out both.
 */
import { ArrowPathRoundedSquareIcon } from '@heroicons/react/24/outline';
import type { ModelConfig } from '../../types';
import type { AccountModelPicks } from '../../hooks/useAccountModels';
import { ModelConfigPickerOptions } from './ModelConfigPickerOptions';

export const SAME_PROVIDER_NOTE = 'Same provider as the primary: a provider outage will affect both';

const providerOf = (config: ModelConfig | undefined) => config?.model.provider?.id ?? config?.model.providerId;

/** True when both configs are known and run on the same provider. */
export function sharesProvider(configs: ModelConfig[], primaryId: string | null | undefined, failoverId: string | null | undefined) {
  if (!primaryId || !failoverId) return false;
  const primary = providerOf(configs.find((c) => c.id === primaryId));
  const failover = providerOf(configs.find((c) => c.id === failoverId));
  return !!primary && primary === failover;
}

interface FailoverModelConfigPickerProps {
  configs: ModelConfig[];
  policy?: AccountModelPicks | null;
  primaryId: string | null | undefined;
  value: string | null | undefined;
  /** Null when "No failover" is chosen. */
  onChange: (failoverModelConfigId: string | null) => void;
  disabled?: boolean;
}

export function FailoverModelConfigPicker({
  configs,
  policy,
  primaryId,
  value,
  onChange,
  disabled,
}: FailoverModelConfigPickerProps) {
  return (
    <div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <ArrowPathRoundedSquareIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <label htmlFor="failoverModelConfigId" className="text-sm text-gray-500 dark:text-gray-400">
            Failover:
          </label>
        </div>
        <select
          id="failoverModelConfigId"
          name="failoverModelConfigId"
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          className="flex-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
        >
          <option value="">No failover</option>
          <ModelConfigPickerOptions
            configs={configs}
            selectedId={value}
            policy={policy}
            unavailable={{ ids: [primaryId], reason: 'the primary' }}
            label={(config) => `${config.name || config.model.modelId} (${config.model.provider?.key ?? 'unknown'})`}
          />
        </select>
      </div>
      {sharesProvider(configs, primaryId, value) && (
        <p data-testid="failover-same-provider-note" className="mt-1 text-xs text-amber-700 dark:text-amber-300">
          {SAME_PROVIDER_NOTE}
        </p>
      )}
    </div>
  );
}

export default FailoverModelConfigPicker;
